import { useCallback, useEffect, useState } from "react";
import api from "@/api/client";

// Convert URL-safe base64 → Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

const isSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// Register the service worker once per app lifecycle (idempotent)
let swRegistrationPromise = null;
function ensureServiceWorker() {
  if (!isSupported()) return Promise.reject(new Error("unsupported"));
  if (!swRegistrationPromise) {
    swRegistrationPromise = navigator.serviceWorker.register("/sw.js", { scope: "/" });
  }
  return swRegistrationPromise;
}

export default function usePushNotifications({ userId } = {}) {
  const [supported] = useState(isSupported());
  const [permission, setPermission] = useState(supported ? Notification.permission : "unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refreshSubscribed = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await ensureServiceWorker();
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch { /* ignore */ }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    setPermission(Notification.permission);
    refreshSubscribed();
  }, [supported, refreshSubscribed, userId]);

  const enable = useCallback(async () => {
    if (!supported) { setError("متصفحك لا يدعم الإشعارات"); return { ok: false }; }
    setBusy(true); setError("");
    try {
      // 1. permission
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { setBusy(false); setError("تم رفض إذن الإشعارات"); return { ok: false }; }

      // 2. sw
      const reg = await ensureServiceWorker();
      // Wait for it to be active
      if (!reg.active) {
        await new Promise((resolve) => {
          const sw = reg.installing || reg.waiting;
          if (!sw) return resolve();
          sw.addEventListener("statechange", (e) => { if (e.target.state === "activated") resolve(); });
        });
      }

      // 3. VAPID key from server
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.publicKey) throw new Error("مفتاح VAPID غير متوفر");

      // 4. Subscribe (or reuse)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }

      // 5. Send to backend
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
      });
      setSubscribed(true);
      setBusy(false);
      return { ok: true };
    } catch (e) {
      setError(e?.message || "فشل تفعيل الإشعارات");
      setBusy(false);
      return { ok: false, error: e?.message };
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await ensureServiceWorker();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        try { await api.post("/push/unsubscribe", { endpoint: json.endpoint, keys: json.keys }); } catch {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally { setBusy(false); }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try { await api.post("/push/test"); return { ok: true }; }
    catch (e) { return { ok: false, error: e?.message }; }
  }, []);

  return { supported, permission, subscribed, busy, error, enable, disable, sendTest };
}
